import { useCallback, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { cn } from '@repo/ui/src/lib/utils';
import type { NoteTemplateItem } from '@repo/shared-types';

/** A saved item whose template row no longer exists: shown read-only with its
 *  snapshotted name so nothing is dropped silently, still clearable. */
export interface OrphanItem { itemId: string; name: string }

/**
 * E2's label-left table (handoff 19 §5, port guide 20 §3): one row per
 * template item, 96px label, an auto-growing textarea with no border of its
 * own; the focused row tints brand-50/50 and its label brand-700. Used for
 * common homework, per-student items and per-student homework. Values live in
 * the caller (`Record<itemId, string>`).
 */
export function NoteItemTable({ items, values, onChange, idPrefix, orphans = [], focusRef, focusItemId, footer }: {
  items: NoteTemplateItem[];
  values: Record<string, string>;
  onChange: (itemId: string, value: string) => void;
  idPrefix: string;
  orphans?: OrphanItem[];
  focusRef?: RefObject<HTMLTextAreaElement | null>;
  focusItemId?: string | null;
  /** THI-196: the homework photo strip renders as the card's last row. */
  footer?: ReactNode;
}) {
  const { t } = useTranslation('progress');
  return (
    <div className="overflow-hidden rounded-md border border-surface-300 bg-surface-0 shadow-xs">
      {items.map((it) => (
        <Row
          key={it.id}
          id={`${idPrefix}-${it.id}`}
          name={it.name}
          hint={it.hint ?? ''}
          value={values[it.id] ?? ''}
          onChange={(v) => onChange(it.id, v)}
          textareaRef={focusItemId === it.id ? focusRef : undefined}
        />
      ))}
      {orphans.map((o) => (
        <Row
          key={o.itemId}
          id={`${idPrefix}-${o.itemId}`}
          name={o.name}
          hint=""
          value={values[o.itemId] ?? ''}
          onChange={(v) => onChange(o.itemId, v)}
          muted
          title={t('notes.item.orphan')}
        />
      ))}
      {footer}
    </div>
  );
}

function Row({ id, name, hint, value, onChange, muted = false, title, textareaRef }: {
  id: string; name: string; hint: string; value: string; onChange: (v: string) => void;
  muted?: boolean; title?: string; textareaRef?: RefObject<HTMLTextAreaElement | null>;
}) {
  // `field-sizing: content` grows the box in Chrome/Safari; Firefox gets the
  // scrollHeight fallback below. No library (port guide 20 §3.1).
  const grow = useCallback((el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  return (
    <div
      className={cn(
        'group grid grid-cols-[96px_1fr] items-start border-b border-surface-100 last:border-b-0 focus-within:bg-brand-50/50',
        muted && 'opacity-70',
      )}
      title={title}
    >
      <label
        htmlFor={id}
        className={cn(
          'pl-3 pt-[11px] text-[12.5px] font-medium leading-[1.3] text-surface-500 group-focus-within:text-brand-700 md:pt-[10px]',
        )}
      >
        {name}
      </label>
      <Textarea
        id={id}
        ref={textareaRef}
        rows={1}
        value={value}
        placeholder={hint}
        onChange={(e) => { grow(e.currentTarget); onChange(e.target.value); }}
        onInput={(e) => grow(e.currentTarget)}
        className="min-h-[44px] resize-none border-0 bg-transparent py-2.5 pl-1 pr-3 text-[15px] leading-[1.45] shadow-none [field-sizing:content] focus-visible:ring-0 md:min-h-[40px] md:text-sm"
      />
    </div>
  );
}
